import { ApiResult, HttpClient } from "../http.js";
import {
  CreateProjectDefectResponse,
  CreateProjectDefectsBulkResponse,
  DefectDto,
  GuestExternalDefectBatchDto,
  GuestPhotoUploadResultDto,
  ResolveGuestExternalDefectResponse,
  DefectReportPayload,
  DefectReportResult,
} from "../types/defects.js";

export interface CreateDefectPayload {
  title: string;
  description?: string;
  location?: string;
  deadline?: string;
  trade?: string;
  assigneeType?: "internal" | "external";
  assignedTradeId?: string;
  assignedUserId?: string;
  assigneeName?: string;
  assigneeEmail?: string;
}

export interface CreateBulkTradeAssignmentPayload {
  trade: string;
  assignedTradeId?: string;
  assigneeType: "internal" | "external";
  assignedUserId?: string;
  assigneeName?: string;
  assigneeEmail?: string;
}

export interface CreateBulkDefectsPayload {
  defects: CreateDefectPayload[];
  tradeAssignments?: CreateBulkTradeAssignmentPayload[];
  /**
   * Stable per-draft idempotency key. A retried submit (e.g. after a lost response) with the
   * same key returns the original batches instead of creating duplicates. Generated client-side
   * and reused across retries of the same draft.
   */
  idempotencyKey?: string;
}

export interface UpdateDefectPayload {
  title?: string;
  description?: string;
  location?: string;
  deadline?: string;
  trade?: string;
  assignedTradeId?: string;
  assignedUserId?: string;
}

export interface ResolveDefectPayload {
  resolution?: string | null;
}

export interface ResolveGuestDefectPayload {
  completionPhotoIds: string[];
  resolution?: string | null;
}

export interface ContinueGuestDefectPayload {
  completionPhotoIds: string[];
  resolutionNotes?: string | null;
  nextTrade: string;
  nextAssigneeType: "internal" | "external";
  nextAssignedTradeId?: string;
  nextAssigneeId?: string;
  nextAssigneeName: string;
  nextAssigneeEmail?: string;
  nextDescription: string;
  nextDeadline?: string | null;
}

export class DefectsApi {
  constructor(private readonly http: HttpClient) {}

  listByProject(projectId: string): Promise<ApiResult<DefectDto[]>> {
    return this.http.get<DefectDto[]>(`/api/projects/${encodeURIComponent(projectId)}/defects`);
  }

  create(projectId: string, payload: CreateDefectPayload): Promise<ApiResult<CreateProjectDefectResponse>> {
    return this.http.post<CreateProjectDefectResponse>(`/api/projects/${encodeURIComponent(projectId)}/defects`, {
      body: payload,
    });
  }

  createBulk(projectId: string, payload: CreateBulkDefectsPayload): Promise<ApiResult<CreateProjectDefectsBulkResponse>> {
    return this.http.post<CreateProjectDefectsBulkResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/defects/bulk`,
      { body: payload },
    );
  }

  /**
   * Builds the mangelrapport for a project. `preview` returns a signed URL to the
   * PDF (valid one hour); `send` mails it to `recipients` with the PDF attached.
   *
   * Replaces the `defectReport` Cloud Function, which required a Firebase session
   * the app has not carried since the RsConnect cutover and answered 401 for every
   * caller (#2121).
   */
  report(projectId: string, payload: DefectReportPayload): Promise<ApiResult<DefectReportResult>> {
    return this.http.post<DefectReportResult>(
      `/api/projects/${encodeURIComponent(projectId)}/defect-report`,
      { body: payload },
    );
  }

  get(defectId: string): Promise<ApiResult<DefectDto>> {
    return this.http.get<DefectDto>(`/api/defects/${encodeURIComponent(defectId)}`);
  }

  update(defectId: string, payload: UpdateDefectPayload): Promise<ApiResult<DefectDto>> {
    return this.http.patch<DefectDto>(`/api/defects/${encodeURIComponent(defectId)}`, { body: payload });
  }

  delete(defectId: string): Promise<ApiResult<void>> {
    return this.http.delete<void>(`/api/defects/${encodeURIComponent(defectId)}`);
  }

  start(defectId: string): Promise<ApiResult<DefectDto>> {
    return this.http.post<DefectDto>(`/api/defects/${encodeURIComponent(defectId)}/start`);
  }

  resolve(defectId: string, payload: ResolveDefectPayload): Promise<ApiResult<DefectDto>> {
    return this.http.post<DefectDto>(`/api/defects/${encodeURIComponent(defectId)}/resolve`, { body: payload });
  }

  close(defectId: string): Promise<ApiResult<DefectDto>> {
    return this.http.post<DefectDto>(`/api/defects/${encodeURIComponent(defectId)}/close`);
  }

  reopen(defectId: string): Promise<ApiResult<DefectDto>> {
    return this.http.post<DefectDto>(`/api/defects/${encodeURIComponent(defectId)}/reopen`);
  }

  resolveGuestBatch(token: string): Promise<ApiResult<GuestExternalDefectBatchDto>> {
    return this.http.get<GuestExternalDefectBatchDto>("/api/guests/external/defect-batches/resolve", {
      query: { token },
    });
  }

  resolveGuestDefect(token: string): Promise<ApiResult<ResolveGuestExternalDefectResponse>> {
    return this.http.get<ResolveGuestExternalDefectResponse>("/api/guests/external/defect-items/resolve", {
      query: { token },
    });
  }

  uploadGuestPhoto(defectId: string, token: string, file: FormData): Promise<ApiResult<GuestPhotoUploadResultDto>> {
    return this.http.post<GuestPhotoUploadResultDto>(
      `/api/guests/external/defect-items/${encodeURIComponent(defectId)}/photos`,
      {
        query: { token },
        body: file,
      },
    );
  }

  resolveGuest(defectId: string, token: string, payload: ResolveGuestDefectPayload): Promise<ApiResult<{ success: boolean }>> {
    return this.http.post<{ success: boolean }>(
      `/api/guests/external/defect-items/${encodeURIComponent(defectId)}/resolve`,
      {
        query: { token },
        body: payload,
      },
    );
  }

  continueGuest(
    defectId: string,
    token: string,
    payload: ContinueGuestDefectPayload,
  ): Promise<ApiResult<{ success: boolean; newToken: string | null; chainPosition: number }>> {
    return this.http.post<{ success: boolean; newToken: string | null; chainPosition: number }>(
      `/api/guests/external/defect-items/${encodeURIComponent(defectId)}/continue`,
      {
        query: { token },
        body: payload,
      },
    );
  }
}
